import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { playerEvents } from "../lib/api";
import { GAMES, getGameColor, getGameName } from "../constants/theme";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dices, MapPin, Calendar, Users, Search, Lock } from "lucide-react";

export const NearbyGamesSection = ({ isAuthenticated }) => {
  const [events, setEvents] = useState([]);
  const [game, setGame] = useState("all");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = {};
    if (isAuthenticated && game !== "all") params.game = game;
    if (isAuthenticated && city.trim()) params.city = city.trim();
    setLoading(true);
    playerEvents.list(params).then((r) => setEvents(r.data || [])).catch(() => setEvents([])).finally(() => setLoading(false));
  }, [game, city, isAuthenticated]);

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="nearby-games-section">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="text-white font-bold flex items-center gap-2"><Dices className="w-5 h-5 text-[#7B68EE]" />Parties près de chez moi</h2>
        <span className="text-[#666] text-xs">{events.length} partie{events.length > 1 ? "s" : ""}</span>
      </div>

      {isAuthenticated ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          <Select value={game} onValueChange={setGame}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white h-9" data-testid="nearby-game-filter"><SelectValue placeholder="Jeu" /></SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#333]">
              <SelectItem value="all" className="text-white">Tous les jeux</SelectItem>
              {GAMES.map((g) => <SelectItem key={g.id} value={g.id} className="text-white">{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 text-[#666] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ville..." className="bg-[#252525] border-[#333] text-white pl-9 h-9" data-testid="nearby-city-filter" />
          </div>
        </div>
      ) : (
        <Link to="/login" className="flex items-center justify-center gap-2 mb-4 px-4 py-3 rounded-lg bg-[#252525] border border-[#333] text-[#B0B0B0] hover:text-white text-sm transition-colors" data-testid="nearby-login-cta">
          <Lock className="w-4 h-4" />Connectez-vous pour rechercher par jeu et par ville
        </Link>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#7B68EE]" /></div>
      ) : events.length === 0 ? (
        <p className="text-[#666] text-sm text-center py-6">Aucune partie à venir</p>
      ) : (
        <div className="grid gap-2">
          {events.slice(0, 8).map((e) => (
            <Link key={e.id} to={`/game/${e.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#252525] hover:bg-[#2c2c2c] transition-colors" data-testid={`nearby-game-${e.id}`}>
              <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: getGameColor(e.game) }} />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{e.title || `Partie ${getGameName(e.game)}`}</p>
                <div className="flex items-center gap-3 text-xs text-[#888] mt-0.5 flex-wrap">
                  <span style={{ color: getGameColor(e.game) }}>{getGameName(e.game)}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(e.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                  {e.location?.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location.city}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{e.confirmed_count}/{e.max_players}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default NearbyGamesSection;
